<script setup lang="ts">
import ChatDiagnosisTemplateFooter from './ChatDiagnosisTemplateFooter.vue';
import ChatDiagnosisTemplateInputItemCheckboxes, {
  type CheckBoxOption,
} from './ChatDiagnosisTemplateInputItem/ChatDiagnosisTemplateInputItemCheckboxes.vue';
import ChatDiagnosisTemplateInputItemDropdown from './ChatDiagnosisTemplateInputItem/ChatDiagnosisTemplateInputItemDropdown.vue';
import ChatDiagnosisTemplateInputItemNumberText from './ChatDiagnosisTemplateInputItem/ChatDiagnosisTemplateInputItemNumberText.vue';
import ChatDiagnosisTemplateInputItemPostalCode from './ChatDiagnosisTemplateInputItem/ChatDiagnosisTemplateInputItemPostalCode.vue';
import ChatDiagnosisTemplateInputItemRadioBlocks from './ChatDiagnosisTemplateInputItem/ChatDiagnosisTemplateInputItemRadioBlocks.vue';
import ChatDiagnosisTemplateInputItemTextArea from './ChatDiagnosisTemplateInputItem/ChatDiagnosisTemplateInputItemTextArea.vue';
import type { Answers } from './composables/constants';
import { useCreateDiagnosis } from './composables/useCreateDiagnosis';
import { useDiagnosisQuestionScroll } from './composables/useDiagnosisQuestionScroll';
import { useDiagnosisQuestions } from './composables/useDiagnosisQuestions';
import { useDiagnosisValidation } from './composables/useDiagnosisValidation';
import { useDiagnosisVisibleState } from './composables/useDiagnosisVisibleState';
import { useSetStoredDiagnosisAnswers } from './composables/useStoredDiagnosisAnswers';
import BotPlainChatMessage from '@/components/organisms/ChatMessage/BotPlainChatMessage.vue';
import { karteTopicsLabelMap } from '@/entity/karteTopics';
import { type FragmentType, graphql } from '@/gql';
import { Gender, KarteTopic2 } from '@/gql/graphql';
import { useEventListener } from '@vueuse/core';
import type { Option as DropdownOption } from 'theme/components/atoms/SelectorDropdown.vue';
import type { RadioBlockOption } from 'theme/components/molecules/RadioBlock.vue';
import VerticalCollapseTransition from 'theme/components/transitions/VerticalCollapseTransition.vue';
import { stringToBase64 } from 'theme/utils/base64';
import { Channel } from '~/gql/graphql';

const props = defineProps<{
  viewer?: FragmentType<typeof ChatDiagnosisTemplateQuestionsViewerFragment>;
  jobs: FragmentType<typeof ChatDiagnosisTemplateQuestionsJobsFragment>[];
  initialAnswers?: Answers;
}>();

const ChatDiagnosisTemplateQuestionsViewerFragment = graphql(`
  fragment ChatDiagnosisTemplateQuestionsViewer on ViewerQueryPayload {
    ... on ViewerQuerySuccessPayload {
      result {
        id
        currentChannel
        currentKarte {
          id
        }
      }
    }
  }
`);

const ChatDiagnosisTemplateQuestionsJobsFragment = graphql(`
  fragment ChatDiagnosisTemplateQuestionsJobs on Job {
    id
    name
  }
`);

const viewer = useComputedFragment(
  ChatDiagnosisTemplateQuestionsViewerFragment,
  () => props.viewer
);

const jobs = useComputedFragment(
  ChatDiagnosisTemplateQuestionsJobsFragment,
  () => props.jobs
);

const router = useRouter();
const alert = useAlert();

const genderOptions: RadioBlockOption<Gender, string>[] = [
  {
    value: Gender.Male,
    content: '男性',
  },
  {
    value: Gender.Female,
    content: '女性',
  },
];

const childGenderOptions: RadioBlockOption<Gender, string>[] = [
  {
    value: Gender.Male,
    content: '男の子',
  },
  {
    value: Gender.Female,
    content: '女の子',
  },
];

const existsOptions: RadioBlockOption<boolean, string>[] = [
  {
    value: true,
    content: 'いる',
  },
  {
    value: false,
    content: 'いない',
  },
];

const booleanOptions: RadioBlockOption<boolean, string>[] = [
  {
    value: true,
    content: 'はい',
  },
  {
    value: false,
    content: 'いいえ',
  },
];

const countOfChildrenOptions: DropdownOption<1 | 2 | 3 | 4 | 5>[] = [
  {
    type: 'item',
    value: 1,
    title: '1人',
  },
  {
    type: 'item',
    value: 2,
    title: '2人',
  },
  {
    type: 'item',
    value: 3,
    title: '3人',
  },
  {
    type: 'item',
    value: 4,
    title: '4人',
  },
  {
    type: 'item',
    value: 5,
    title: '5人',
  },
];

const jobOptions: DropdownOption<string>[] = jobs.value.map((job) => ({
  type: 'item',
  value: job.id,
  title: job.name,
}));

const karteTopicOptions: CheckBoxOption<KarteTopic2, string>[] = [
  KarteTopic2.FamilyBudgetImprovement,
  KarteTopic2.LifePlanning,
  KarteTopic2.AssetManagement,
  KarteTopic2.RealEstateInvestment,
  KarteTopic2.Insurance,
  KarteTopic2.EducationExpenses,
  KarteTopic2.Retirement,
  KarteTopic2.BuyingAHouse,
  KarteTopic2.Mortgage,
  KarteTopic2.Inheritance,
  KarteTopic2.JobChange,
  KarteTopic2.Debt,
].map((topic) => {
  return {
    value: topic,
    content: karteTopicsLabelMap[topic],
  };
});

const {
  answers,
  answeredQuestions,
  numberOfQuestionsNeedToAnswer,
  enabledQuestions,
  addressOptions,
} = useDiagnosisQuestions(props.initialAnswers);
const { isValid, errors, lazyErrorFields, errorFields } =
  useDiagnosisValidation(answers, enabledQuestions);
const { formInputVisibleStates } = useDiagnosisVisibleState(
  answers,
  enabledQuestions,
  errorFields
);
const { questionElementIDList } = useDiagnosisQuestionScroll(
  formInputVisibleStates
);
const { create, createDiagnosisResult } = useCreateDiagnosis(answers);
const aiMatchScoreResult = computed(
  () =>
    createDiagnosisResult.value?.createDiagnosisV20240229AndKarte
      ?.latestDiagnosis?.aiMatchScore
);

const categoryAffinities = useCategoryAffinities(aiMatchScoreResult);

const { clearStoredAnswers } = useSetStoredDiagnosisAnswers(answers);

const { logger } = useLogger();
type LogArgsDiagnosisClusterStatistic = NonNullable<
  Parameters<
    NonNullable<(typeof logger)['value']>['event']['finishDiagnosis']
  >[0]
>['diagnosisClusterStatistic'];

const logArgsDiagnosisClusterStatistic =
  computed<LogArgsDiagnosisClusterStatistic>(() => {
    const diagnosis =
      createDiagnosisResult.value?.createDiagnosisV20240229AndKarte
        ?.latestDiagnosis;
    if (diagnosis?.aiMatchScore == null || diagnosis.resultV2 == null)
      // NOTE: 現状特定のチャネルでしか202406診断リニューアルにおける統計情報等を提示していないため
      return undefined;
    return {
      householdStructure: diagnosis.resultV2.cluster.householdStructure,
      residentialArea: {
        // ロジック的にprefectureが入らないことはないらしいけど一応空文字列に丸める
        prefecture: diagnosis.resultV2.cluster.residentialArea.prefecture ?? '',
        city: diagnosis.resultV2.cluster.residentialArea.city ?? undefined,
        town: diagnosis.resultV2.cluster.residentialArea.town ?? undefined,
      },
      rankInCluster: {
        annualIncome: diagnosis.resultV2.householdAnnualIncomeRank,
        totalAssets: diagnosis.resultV2.assetsRank,
        monthlySavings: diagnosis.resultV2.monthlySavingRank,
      },
      type: diagnosis.resultV2.type,
      aiMatchScore: {
        lifePlanning: diagnosis.aiMatchScore.lifePlanningMatchingScore,
        onetimeSale: diagnosis.aiMatchScore.onetimeSaleMatchingScore,
        householdImprovement:
          diagnosis.aiMatchScore.householdImprovementMatchingScore,
        sellingHome: diagnosis.aiMatchScore.sellingHomeMatchingScore,
        realEstateInvestment:
          diagnosis.aiMatchScore.realEstateInvestmentMatchingScore,
        jobChange: diagnosis.aiMatchScore.jobChangeMatchingScore,
      },
      expectedContractPriceScore: {
        lifePlanning:
          diagnosis.aiMatchScore.lifePlanningExpectedContractPriceScore,
        onetimeSale:
          diagnosis.aiMatchScore.onetimeSaleExpectedContractPriceScore,
        householdImprovement:
          diagnosis.aiMatchScore.householdImprovementExpectedContractPriceScore,
        sellingHome:
          diagnosis.aiMatchScore.sellingHomeExpectedContractPriceScore,
        realEstateInvestment:
          diagnosis.aiMatchScore.realEstateInvestmentExpectedContractPriceScore,
        jobChange: diagnosis.aiMatchScore.jobChangeExpectedContractPriceScore,
      },
      categoryAffinity: {
        lifePlanning: categoryAffinities.lifePlanning.value,
        onetimeSale: categoryAffinities.assetManagement.value,
        householdImprovement: categoryAffinities.householdImprovement.value,
        sellingHome: categoryAffinities.sellingHome.value,
        realEstateInvestment: categoryAffinities.realEstateInvestment.value,
        jobChange: categoryAffinities.jobChange.value,
      },
    };
  });

const { trackEvent } = useGtm();

const currentChannel = computed(() =>
  viewer.value?.__typename === 'ViewerQuerySuccessPayload'
    ? viewer.value?.result.currentChannel
    : Channel.Okaneko
);

const { isNewDiagnosisResultTarget } = useNewDiagnosisResultTarget(
  () => currentChannel.value
);

const submitting = ref(false);
const onSubmit = async (e: Event) => {
  try {
    submitting.value = true;
    e.preventDefault();
    if (!isValid.value) {
      return;
    }
    const result = await create();
    if (result.ok === false) {
      alert.error(
        '診断結果の作成に失敗しました。申し訳ございませんが、時間をおいて再度お試しください。'
      );
      return;
    }
    logger.value?.event.finishDiagnosis({
      diagnosisUuid: result.uuid,
      diagnosisClusterStatistic: logArgsDiagnosisClusterStatistic.value,
    });
    if (
      viewer.value?.__typename === 'ViewerQuerySuccessPayload' &&
      viewer.value.result.currentKarte == null
    ) {
      const encodedUserId = stringToBase64(viewer.value.result.id);
      trackEvent({
        event: 'created-first-karte',
        'user-id': encodedUserId,
      });
    }

    await router.push({
      path: isNewDiagnosisResultTarget.value
        ? '/home'
        : `/chat/diagnosis/result/${result.uuid}`,
    });
    clearStoredAnswers();
  } finally {
    submitting.value = false;
  }
};

const errorsCount = computed(() =>
  answeredQuestions.value.length === numberOfQuestionsNeedToAnswer.value
    ? errors.value.length
    : 0
);

useEventListener(window, 'unload', () => {
  logger.value?.event.abortDiagnosis({
    diagnosis: {
      idx: answeredQuestions.value.length.toString(),
    },
  });
});
</script>

<template>
  <section :class="$style.page">
    <BotPlainChatMessage
      >あなたに近い人と比較するため、まずはあなたのことを聞いていくにゃ！<br /><br />全部で20問くらいにゃ。
    </BotPlainChatMessage>
    <form
      :class="[$style.page__questions, $style.questions]"
      @submit="onSubmit"
    >
      <ChatDiagnosisTemplateInputItemRadioBlocks
        v-show="formInputVisibleStates.gender !== 'hidden'"
        :id="questionElementIDList.gender"
        v-model="answers.gender"
        title="性別"
        :options="genderOptions"
        name="gender"
        :inactive="false"
        :class="$style.questions__item"
      ></ChatDiagnosisTemplateInputItemRadioBlocks>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemDropdown
          v-show="formInputVisibleStates.jobId !== 'hidden'"
          :id="questionElementIDList.jobId"
          v-model="answers.jobId"
          title="ご職業"
          name="job"
          placeholder="選択してください"
          :options="jobOptions"
          :inactive="formInputVisibleStates.jobId === 'inactive'"
        ></ChatDiagnosisTemplateInputItemDropdown>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemPostalCode
          v-show="formInputVisibleStates.postalCode !== 'hidden'"
          :id="questionElementIDList.postalCode"
          v-model="answers.postalCode"
          :error="lazyErrorFields.postalCode"
          title="郵便番号"
          name="postalCode"
          :notes="['※ハイフンなしで入力してください。']"
          placeholder="例）1030026"
          :inactive="formInputVisibleStates.postalCode === 'inactive'"
        ></ChatDiagnosisTemplateInputItemPostalCode>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="formInputVisibleStates.address !== 'hidden'"
          :id="questionElementIDList.address"
          v-model="answers.address"
          title="住所選択"
          :options="addressOptions"
          name="address"
          :inactive="formInputVisibleStates.hasPartner === 'inactive'"
          single-column
        >
          <template #default="{ content: { prefecture, city, town } }">
            {{ prefecture }}{{ city }}{{ town }}
          </template>
        </ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.birthday !== 'hidden'"
          :id="questionElementIDList.birthday"
          v-model="answers.birthday"
          :error="lazyErrorFields.birthday"
          title="生年月日"
          name="birthday"
          :notes="['※西暦＋月日を8桁で教えてください。']"
          placeholder="例）19850423"
          :inactive="formInputVisibleStates.birthday === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.annualIncome !== 'hidden'"
          :id="questionElementIDList.annualIncome"
          v-model="answers.annualIncome"
          :error="lazyErrorFields.annualIncome"
          title="年収"
          name="annualIncome"
          placeholder="例）400"
          unit="万円"
          :inactive="formInputVisibleStates.annualIncome === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.savings !== 'hidden'"
          :id="questionElementIDList.savings"
          v-model="answers.savings"
          :error="lazyErrorFields.savings"
          title="貯金額（資産運用額を除く）"
          name="savings"
          :notes="['※「現金で保有している資産」を入力してください。']"
          placeholder="例）200"
          unit="万円"
          :inactive="formInputVisibleStates.savings === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.monthlySavings !== 'hidden'"
          :id="questionElementIDList.monthlySavings"
          v-model="answers.monthlySavings"
          :error="lazyErrorFields.monthlySavings"
          title="毎月の貯金額（資産運用額を除く）"
          name="monthlySavings"
          :notes="[
            '※「現金の貯蓄額」を入力してください。保険や投資の金額は別途後で入力する項目があります。',
          ]"
          placeholder="例）3"
          unit="万円/月"
          :inactive="formInputVisibleStates.monthlySavings === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.investment !== 'hidden'"
          :id="questionElementIDList.investment"
          v-model="answers.investment"
          :error="lazyErrorFields.investment"
          title="資産運用の合計額"
          name="investment"
          :notes="[
            '※現金を除いた、株や債券などのリスク性資産の時価総額を入力してください。詳細がすぐわからない場合は、だいたいの金額でも問題ありません。',
          ]"
          placeholder="例）300"
          unit="万円"
          :inactive="formInputVisibleStates.investment === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <BotPlainChatMessage
          v-show="formInputVisibleStates.hasPartner !== 'hidden'"
          >順調にゃ。診断結果が楽しみにゃ。続けて、ご家族や出費について聞いていくにゃ。
        </BotPlainChatMessage>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="formInputVisibleStates.hasPartner !== 'hidden'"
          :id="questionElementIDList.hasPartner"
          v-model="answers.hasPartner"
          title="配偶者・パートナー"
          :options="existsOptions"
          name="hasPartner"
          :inactive="formInputVisibleStates.hasPartner === 'inactive'"
        ></ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.partnerBirthday !== 'hidden'"
          :id="questionElementIDList.partnerBirthday"
          v-model="answers.partnerBirthday"
          :error="lazyErrorFields.partnerBirthday"
          title="配偶者・パートナーの誕生日"
          name="partnerBirthday"
          :notes="['※西暦＋月日を8桁で教えてください。']"
          placeholder="例）19850423"
          :inactive="formInputVisibleStates.partnerBirthday === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.partnerAnnualIncome !== 'hidden'"
          :id="questionElementIDList.partnerAnnualIncome"
          v-model="answers.partnerAnnualIncome"
          :error="lazyErrorFields.partnerAnnualIncome"
          title="配偶者・パートナーの年収"
          name="partnerAnnualIncome"
          placeholder="例）400"
          unit="万円"
          :inactive="formInputVisibleStates.partnerAnnualIncome === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="formInputVisibleStates.hasChild !== 'hidden'"
          :id="questionElementIDList.hasChild"
          v-model="answers.hasChild"
          title="お子様"
          name="hasChild"
          :options="existsOptions"
          :inactive="formInputVisibleStates.hasChild === 'inactive'"
        ></ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemDropdown
          v-show="formInputVisibleStates.countOfChildren !== 'hidden'"
          :id="questionElementIDList.countOfChildren"
          v-model="answers.countOfChildren"
          title="お子様の人数"
          name="countOfChildren"
          placeholder="選択してください"
          :options="countOfChildrenOptions"
          :inactive="formInputVisibleStates.countOfChildren === 'inactive'"
        ></ChatDiagnosisTemplateInputItemDropdown>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.firstChildBirthDay !== 'hidden'"
          :id="questionElementIDList.firstChildBirthDay"
          v-model="answers.firstChildBirthDay"
          :error="lazyErrorFields.firstChildBirthDay"
          title="1人目のお子様の生年月日"
          name="firstChildBirthDay"
          :notes="['※西暦＋月日を8桁で教えてください。']"
          placeholder="例）20010423"
          :inactive="formInputVisibleStates.firstChildBirthDay === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.secondChildBirthDay !== 'hidden'"
          :id="questionElementIDList.secondChildBirthDay"
          v-model="answers.secondChildBirthDay"
          :error="lazyErrorFields.secondChildBirthDay"
          title="2人目のお子様の生年月日"
          name="secondChildBirthDay"
          :notes="['※西暦＋月日を8桁で教えてください。']"
          placeholder="例）20010423"
          :inactive="formInputVisibleStates.secondChildBirthDay === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.thirdChildBirthDay !== 'hidden'"
          :id="questionElementIDList.thirdChildBirthDay"
          v-model="answers.thirdChildBirthDay"
          :error="lazyErrorFields.thirdChildBirthDay"
          title="3人目のお子様の生年月日"
          name="thirdChildBirthDay"
          :notes="['※西暦＋月日を8桁で教えてください。']"
          placeholder="例）20010423"
          :inactive="formInputVisibleStates.thirdChildBirthDay === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.fourthChildBirthDay !== 'hidden'"
          :id="questionElementIDList.fourthChildBirthDay"
          v-model="answers.fourthChildBirthDay"
          :error="lazyErrorFields.fourthChildBirthDay"
          title="4人目のお子様の生年月日"
          name="fourthChildBirthDay"
          :notes="['※西暦＋月日を8桁で教えてください。']"
          placeholder="例）20010423"
          :inactive="formInputVisibleStates.fourthChildBirthDay === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.fifthChildBirthDay !== 'hidden'"
          :id="questionElementIDList.fifthChildBirthDay"
          v-model="answers.fifthChildBirthDay"
          :error="lazyErrorFields.fifthChildBirthDay"
          title="5人目のお子様の生年月日"
          name="fifthChildBirthDay"
          :notes="['※西暦＋月日を8桁で教えてください。']"
          placeholder="例）20010423"
          :inactive="formInputVisibleStates.fifthChildBirthDay === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="formInputVisibleStates.firstChildGender !== 'hidden'"
          :id="questionElementIDList.firstChildGender"
          v-model="answers.firstChildGender"
          title="1人目のお子様"
          name="firstChildGender"
          :options="childGenderOptions"
          :inactive="formInputVisibleStates.firstChildGender === 'inactive'"
        ></ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="formInputVisibleStates.secondChildGender !== 'hidden'"
          :id="questionElementIDList.secondChildGender"
          v-model="answers.secondChildGender"
          title="2人目のお子様"
          name="secondChildGender"
          :options="childGenderOptions"
          :inactive="formInputVisibleStates.secondChildGender === 'inactive'"
        ></ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="formInputVisibleStates.thirdChildGender !== 'hidden'"
          :id="questionElementIDList.thirdChildGender"
          v-model="answers.thirdChildGender"
          title="3人目のお子様"
          name="thirdChildGender"
          :options="childGenderOptions"
          :inactive="formInputVisibleStates.thirdChildGender === 'inactive'"
        ></ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="formInputVisibleStates.fourthChildGender !== 'hidden'"
          :id="questionElementIDList.fourthChildGender"
          v-model="answers.fourthChildGender"
          title="4人目のお子様"
          name="fourthChildGender"
          :options="childGenderOptions"
          :inactive="formInputVisibleStates.fourthChildGender === 'inactive'"
        ></ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="formInputVisibleStates.fifthChildGender !== 'hidden'"
          :id="questionElementIDList.fifthChildGender"
          v-model="answers.fifthChildGender"
          title="5人目のお子様"
          name="fifthChildGender"
          :options="childGenderOptions"
          :inactive="formInputVisibleStates.fifthChildGender === 'inactive'"
        ></ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.monthlyRentCost !== 'hidden'"
          :id="questionElementIDList.monthlyRentCost"
          v-model="answers.monthlyRentCost"
          :error="lazyErrorFields.monthlyRentCost"
          title="毎月の住宅費（持ち家の方は毎月の住宅ローン）"
          name="monthlyRentCost"
          :notes="['※同居人がいる場合、個人での負担額を書いてください。']"
          placeholder="例）8"
          unit="万円/月"
          :inactive="formInputVisibleStates.monthlyRentCost === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.loanBalance !== 'hidden'"
          :id="questionElementIDList.loanBalance"
          v-model="answers.loanBalance"
          :error="lazyErrorFields.loanBalance"
          title="借り入れの合計額（住宅ローン、カーローン等）"
          name="loanBalance"
          :notes="[
            '※月額ではなく、総額でご記入ください。金額は大体でOKです。同居人がいる場合は、世帯での負担額を書いてください。',
          ]"
          placeholder="例）3000"
          unit="万円"
          :inactive="formInputVisibleStates.loanBalance === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemNumberText
          v-show="formInputVisibleStates.monthlyInsurance !== 'hidden'"
          :id="questionElementIDList.monthlyInsurance"
          v-model="answers.monthlyInsurance"
          :error="lazyErrorFields.monthlyInsurance"
          title="毎月の保険料"
          name="monthlyInsurance"
          :notes="[
            '※火災保険・自動車保険等も含めた世帯の合計負担額を入力してください。',
          ]"
          placeholder="例）20000"
          unit="円/月"
          :inactive="formInputVisibleStates.monthlyInsurance === 'inactive'"
        ></ChatDiagnosisTemplateInputItemNumberText>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <BotPlainChatMessage
          v-show="
            formInputVisibleStates.thinksToughTimesBringOpportunity !== 'hidden'
          "
          >あとは簡単な質問だけにゃ！
        </BotPlainChatMessage>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="
            formInputVisibleStates.thinksToughTimesBringOpportunity !== 'hidden'
          "
          :id="questionElementIDList.thinksToughTimesBringOpportunity"
          v-model="answers.thinksToughTimesBringOpportunity"
          title="ピンチはチャンスだと思う"
          name="thinksToughTimesBringOpportunity"
          :options="booleanOptions"
          :inactive="
            formInputVisibleStates.thinksToughTimesBringOpportunity ===
            'inactive'
          "
        ></ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="formInputVisibleStates.thinksWhatDoneIsDone !== 'hidden'"
          :id="questionElementIDList.thinksWhatDoneIsDone"
          v-model="answers.thinksWhatDoneIsDone"
          title="投資金額の30%を損しても耐えられる"
          name="thinksWhatDoneIsDone"
          :options="booleanOptions"
          :inactive="formInputVisibleStates.thinksWhatDoneIsDone === 'inactive'"
        ></ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="formInputVisibleStates.isInterestedInNisaOrIdeco !== 'hidden'"
          :id="questionElementIDList.isInterestedInNisaOrIdeco"
          v-model="answers.isInterestedInNisaOrIdeco"
          title="NISAやiDecoに興味がある"
          name="isInterestedInNisaOrIdeco"
          :options="booleanOptions"
          :inactive="
            formInputVisibleStates.isInterestedInNisaOrIdeco === 'inactive'
          "
        ></ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="formInputVisibleStates.hasGoalInFuture !== 'hidden'"
          :id="questionElementIDList.hasGoalInFuture"
          v-model="answers.hasGoalInFuture"
          title="将来の目標や夢がある"
          name="hasGoalInFuture"
          :options="booleanOptions"
          :inactive="formInputVisibleStates.hasGoalInFuture === 'inactive'"
        ></ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="formInputVisibleStates.takesThreeOrMoreLessons !== 'hidden'"
          :id="questionElementIDList.takesThreeOrMoreLessons"
          v-model="answers.takesThreeOrMoreLessons"
          title="3つ以上の習い事をしている"
          name="takesThreeOrMoreLessons"
          :options="booleanOptions"
          :inactive="
            formInputVisibleStates.takesThreeOrMoreLessons === 'inactive'
          "
        ></ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemRadioBlocks
          v-show="formInputVisibleStates.followsTheLatestTrends !== 'hidden'"
          :id="questionElementIDList.followsTheLatestTrends"
          v-model="answers.followsTheLatestTrends"
          title="流行には敏感なほうだ"
          name="followsTheLatestTrends"
          :options="booleanOptions"
          :inactive="
            formInputVisibleStates.followsTheLatestTrends === 'inactive'
          "
        ></ChatDiagnosisTemplateInputItemRadioBlocks>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <BotPlainChatMessage
          v-show="formInputVisibleStates.karteTopics !== 'hidden'"
          >いよいよラスト2問にゃ！
        </BotPlainChatMessage>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemCheckboxes
          v-show="formInputVisibleStates.karteTopics !== 'hidden'"
          :id="questionElementIDList.karteTopics"
          v-model="answers.karteTopics"
          :options="karteTopicOptions"
          :error="lazyErrorFields.karteTopics"
          title="今気になっているトピック"
          name="karteTopics"
          :inactive="formInputVisibleStates.karteTopics === 'inactive'"
        ></ChatDiagnosisTemplateInputItemCheckboxes>
      </VerticalCollapseTransition>
      <VerticalCollapseTransition
        :duration="100"
        :class="$style.questions__item"
      >
        <ChatDiagnosisTemplateInputItemTextArea
          v-show="formInputVisibleStates.karteComment !== 'hidden'"
          :id="questionElementIDList.karteComment"
          v-model="answers.karteComment"
          :error="lazyErrorFields.karteComment"
          title="選んだトピックの気になるところ"
          name="karteComment"
          :notes="['※15文字以上入力してください']"
          placeholder="例：今後何にどのくらいお金が必要かわからないので、ライフプランニングしたい
例：老後や教育資金などの準備のために、家計改善や資産運用のポイントが知りたい"
          :inactive="formInputVisibleStates.karteComment === 'inactive'"
        >
        </ChatDiagnosisTemplateInputItemTextArea>
      </VerticalCollapseTransition>
      <ChatDiagnosisTemplateFooter
        :progress-ratio="
          (answeredQuestions.length - errorsCount) /
          numberOfQuestionsNeedToAnswer
        "
        :disabled="!isValid || submitting"
        @submit="onSubmit"
      ></ChatDiagnosisTemplateFooter>
    </form>
  </section>
</template>

<style lang="scss" module>
.page {
  &__questions {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}

.questions {
  &__item {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}
</style>
